import { useCallback, useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { Container, DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { OrganizationFormModal } from './OrganizationFormModal';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationByIdQuery,
} from '@/services/OrganizationManagementService';
import { OrganizationResponse } from '@/types/organization.types';
import { ErrorApiResponse, PaginatedApiResponse } from '@/types/api.types';
import { usePermissions } from '@/hooks/usePermissions';

export interface IOrganizationData {
  id?: string;
  organisation_name?: string;
  organisation_type?: string;
  contact_email?: string;
  public_homepage?: string;
  description?: string;
  linked_regions?: string[];
  user?: { id: string; name: string; email?: string };
  logo?: string | File | null;
  translations?:
    | Record<
        string,
        Partial<{
          organisation_name: string;
          description: string;
        }>
      >
    | string;
  language?: string;
}

const OrganizationListings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>('create');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);
  const [formData, setFormData] = useState<Partial<IOrganizationData>>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean;
    organization: OrganizationResponse | null;
  }>({ isOpen: false, organization: null });

  const {
    pagination,
    setPagination,
    handleSearch,
    searchTerm,
    resetSearch,
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
  } = usePagination();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue || undefined,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue]
  );

  const {
    data: organizationsData,
    isLoading,
    isFetching,
    isError,
    refetch: refetchOrganizations,
  } = useGetOrganizationsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: PaginatedApiResponse<OrganizationResponse[]> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  const [createOrganization, { isLoading: isCreating }] =
    useCreateOrganizationMutation();
  const [updateOrganization, { isLoading: isUpdating }] =
    useUpdateOrganizationMutation();
  const [deleteOrganization, { isLoading: isDeleting }] =
    useDeleteOrganizationMutation();
  const { data: organizationDetail, isLoading: isOrganizationDetailLoading } =
    useGetOrganizationByIdQuery(selectedOrganizationId!, {
      skip: !selectedOrganizationId,
    });
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (organizationDetail && (mode === 'edit' || mode === 'view')) {
      setFormData({
        id: organizationDetail.data.id,
        organisation_name: organizationDetail.data.organisation_name,
        organisation_type: organizationDetail.data.organisation_type,
        contact_email: organizationDetail.data.contact_email,
        public_homepage: organizationDetail.data.public_homepage,
        description: organizationDetail.data.description,
        translations: organizationDetail.data.translations || undefined,
        language: (organizationDetail.data as { language?: string }).language,
        linked_regions: Array.isArray(organizationDetail.data.linked_regions)
          ? organizationDetail.data.linked_regions.map(
              (region: { id: string }) =>
                typeof region === 'string' ? region : region.id
            )
          : [],
        logo: organizationDetail.data.logo || null,
        user: organizationDetail.data.user
          ? {
              id: organizationDetail.data.user.id,
              name: organizationDetail.data.user.name,
              email: organizationDetail.data.user.email,
            }
          : undefined,
      });
    }
  }, [organizationDetail, mode]);

  const filteredData = useMemo(() => {
    if (!organizationsData?.data) return [];
    return organizationsData.data;
  }, [organizationsData]);

  // --- Pagination Fix: update totalPages and totalItems just like HarborListings/RegionListings ---
  useEffect(() => {
    if (organizationsData) {
      handleTotalPages(organizationsData.totalPages || 1);
      handleTotalItems(organizationsData.totalItems || 0);
    }
  }, [organizationsData, handleTotalPages, handleTotalItems]);

  const handleOpenModal = useCallback(
    (mode: 'view' | 'edit' | 'create', organization?: OrganizationResponse) => {
      setMode(mode);
      setErrors({});
      if (mode === 'create') {
        setFormData({});
      } else if (organization) {
        setSelectedOrganizationId(organization.id);
      }
      setIsModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedOrganizationId(null);
    setFormData({});
    setErrors({});
    refetchOrganizations();
  }, [refetchOrganizations]);

  const handleSubmit = useCallback(
    async (data: Partial<IOrganizationData>) => {
      try {
        const formDataPayload = new FormData();
        const sanitizedTranslations =
          typeof formData.translations === 'string'
            ? formData.translations
            : formData.translations
              ? JSON.stringify(
                  Object.fromEntries(
                    Object.entries(formData.translations).map(
                      ([lang, translation]) => [
                        lang,
                        {
                          description: translation.description,
                        },
                      ]
                    )
                  )
                )
              : undefined;

        if (formData.organisation_name)
          formDataPayload.append(
            'organisation_name',
            formData.organisation_name
          );
        if (formData.organisation_type)
          formDataPayload.append(
            'organisation_type',
            formData.organisation_type
          );
        if (formData.contact_email)
          formDataPayload.append('contact_email', formData.contact_email);
        if (formData.public_homepage)
          formDataPayload.append('public_homepage', formData.public_homepage);
        if (formData.description)
          formDataPayload.append('description', formData.description);
        if (formData.user?.id)
          formDataPayload.append('user_id', formData.user.id);
        if (sanitizedTranslations)
          formDataPayload.append('translations', sanitizedTranslations);
        if (data.language) formDataPayload.append('language', data.language);

        if (formData.logo) {
          if (formData.logo instanceof File) {
            formDataPayload.append('logo', formData.logo);
          } else if (typeof formData.logo === 'string') {
            formDataPayload.append('logo', formData.logo);
          }
        }

        const linked_regions = data.linked_regions || formData.linked_regions;
        if (linked_regions && linked_regions.length > 0) {
          linked_regions.forEach((regionId, index) => {
            formDataPayload.append(`linked_regions[${index}]`, regionId);
          });
        }

        if (mode === 'create') {
          await createOrganization(formDataPayload).unwrap();
          enqueueSnackbar('Organization created successfully', {
            variant: 'success',
          });
        } else if (mode === 'edit' && selectedOrganizationId) {
          const originalLinkedRegions =
            organizationDetail?.data.linked_regions?.map(
              (r: { id: string }) => r.id
            ) || [];
          const currentLinkedRegions =
            data.linked_regions || formData.linked_regions || [];
          const hasRegionChanges =
            JSON.stringify(originalLinkedRegions.sort()) !==
            JSON.stringify(currentLinkedRegions.sort());

          const updateFormData = new FormData();
          if (formData.organisation_name)
            updateFormData.append(
              'organisation_name',
              formData.organisation_name
            );
          if (formData.organisation_type)
            updateFormData.append(
              'organisation_type',
              formData.organisation_type
            );
          if (formData.contact_email)
            updateFormData.append('contact_email', formData.contact_email);
          if (formData.public_homepage)
            updateFormData.append('public_homepage', formData.public_homepage);
          if (formData.description)
            updateFormData.append('description', formData.description);
          if (formData.user?.id)
            updateFormData.append('user_id', formData.user.id);
          if (sanitizedTranslations)
            updateFormData.append('translations', sanitizedTranslations);
          if (data.language) updateFormData.append('language', data.language);

          if (formData.logo) {
            if (formData.logo instanceof File) {
              updateFormData.append('logo', formData.logo);
            } else if (typeof formData.logo === 'string') {
              updateFormData.append('logo', formData.logo);
            }
          }

          if (hasRegionChanges && currentLinkedRegions.length > 0) {
            currentLinkedRegions.forEach((regionId, index) => {
              updateFormData.append(`linked_regions[${index}]`, regionId);
            });
          }

          await updateOrganization({
            organization_id: selectedOrganizationId,
            data: updateFormData,
          }).unwrap();
          enqueueSnackbar('Organization updated successfully', {
            variant: 'success',
          });
        }
        setIsModalOpen(false);
        refetchOrganizations();
      } catch (error) {
        const errorMessage =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'message' in error.data
            ? (error as ErrorApiResponse).data.message
            : error instanceof Error
              ? error.message
              : typeof error === 'string'
                ? error
                : 'Failed to save organization';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [
      mode,
      formData,
      selectedOrganizationId,
      createOrganization,
      updateOrganization,
      enqueueSnackbar,
      refetchOrganizations,
      organizationDetail,
    ]
  );

  const handleDelete = useCallback((organization: OrganizationResponse) => {
    setDeleteDialogState({ isOpen: true, organization });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.organization) return;
    try {
      await deleteOrganization(deleteDialogState.organization.id).unwrap();
      enqueueSnackbar('Organization deleted successfully', {
        variant: 'success',
      });
      refetchOrganizations();
    } catch {
      enqueueSnackbar('Failed to delete organization', { variant: 'error' });
    }
    setDeleteDialogState({ isOpen: false, organization: null });
  }, [
    deleteDialogState,
    deleteOrganization,
    enqueueSnackbar,
    refetchOrganizations,
  ]);

  const handleFilterChange = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [setPagination]);

  const columns: ColumnDef<OrganizationResponse>[] = useMemo(
    () => [
      {
        accessorKey: 'organisationName',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => {
          const organisationName = row.original.organisation_name;
          return (
            <button
              onClick={() => handleOpenModal('view', row.original)}
              className="text-sm font-medium text-gray-900 hover:text-primary-active"
              aria-label={`View ${organisationName}`}
            >
              {organisationName}
            </button>
          );
        },
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row) => row.user?.name || row.user?.email || 'N/A',
        id: 'admin',
        header: 'Admin',
        enableSorting: false,
        meta: {
          className: 'min-w-[180px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenModal('view', row.original)}
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`View ${row.original.organisation_name}`}
            >
              <KeenIcon type="filled" icon="eye" className="text-gray-500" />
            </button>
            {hasPermission('organization_management_update') && (
              <button
                onClick={() => handleOpenModal('edit', row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Edit ${row.original.organisation_name}`}
                data-testid={`edit-org-btn`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('organization_management_delete') && (
              <button
                onClick={() => handleDelete(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Delete ${row.original.organisation_name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-gray-500"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenModal, handleDelete]
  );

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle
              id="organization-management-heading"
              text="Organization Management"
            />
            <ToolbarDescription>
              You can manage your Organizations here.
            </ToolbarDescription>
          </ToolbarHeading>
          {hasPermission('organization_management_create') && (
            <ToolbarActions>
              <button
                className="btn btn-sm btn-light"
                onClick={() => handleOpenModal('create')}
                aria-label="Add new organization"
                data-testid="add-organization-btn"
              >
                Add New Organization
              </button>
            </ToolbarActions>
          )}
        </Toolbar>
      </Container>
      <Container>
        <div className="card card-grid min-w-full">
          <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-organizations"
                aria-label="Search organizations"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-organizations"
                  placeholder="Search organizations"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search organizations"
                />
              </label>
            </div>
            {searchTerm && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                onClick={() => {
                  resetSearch();
                  handleFilterChange();
                }}
                aria-label="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            )}
          </div>
          <div className="card-body">
            {isLoading || isFetching ? (
              <div className="text-center py-4">Loading organizations...</div>
            ) : isError ? (
              <div className="text-center py-4 text-red-500">
                Failed to load organizations. Please try again.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'organisationName', desc: false }]}
                serverSide={true}
                onFilterReset={() => {
                  resetSearch();
                  handleFilterChange();
                }}
                hasActiveFilters={!!searchTerm}
                key={JSON.stringify(queryParams)}
              />
            )}
          </div>
        </div>
      </Container>
      <OrganizationFormModal
        open={isModalOpen}
        onClose={handleCloseModal}
        mode={mode}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isUpdating || isOrganizationDetailLoading}
        isError={
          isOrganizationDetailLoading
            ? false
            : !!(
                mode !== 'create' &&
                organizationDetail === undefined &&
                selectedOrganizationId &&
                !isOrganizationDetailLoading
              )
        }
      />
      <ConfirmationDialog
        open={deleteDialogState.isOpen}
        onClose={() =>
          setDeleteDialogState({ isOpen: false, organization: null })
        }
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${deleteDialogState.organization?.organisation_name || 'this organization'}?`}
        cancelButtonText="No"
        confirmButtonText="Yes"
        disabled={isDeleting}
      />
    </>
  );
};

export default OrganizationListings;
